import { Link } from "@tanstack/react-router";
import type { RouteTrack, StepView } from "@/app/queries";
import { StepMark } from "@/ui/StepMark";
import type { StepState } from "@/ui/route";
import { Eyebrow } from "./bits";
import { tasksTotal, trackTone } from "./chain";

/* Правая колонка трека (S3): место в маршруте, параллельный трек,
   статистика по треку и финальный проект. */

const toneState: Record<ReturnType<typeof trackTone>, StepState> = { done: "done", active: "active", rest: "locked" };

function TrackRow({ t, current, dashed = false }: { t: RouteTrack; current: boolean; dashed?: boolean }) {
  const tone = trackTone(t);
  const fg = tone === "done" ? "text-ok" : tone === "active" ? "text-acc" : "text-mut";
  return (
    <Link
      to="/track/$slug"
      params={{ slug: t.slug }}
      aria-current={current ? "page" : undefined}
      className={`grid grid-cols-[20px_minmax(0,1fr)_auto] items-center gap-2.5 rounded-sm border px-2 py-1.5 no-underline ${current ? "border-acc bg-accsoft" : "border-transparent hover:bg-card2"}`}
      style={dashed ? { borderStyle: "dashed", borderColor: "var(--bd2)" } : undefined}
    >
      <StepMark state={toneState[tone]} />
      <span className={`truncate text-[12.5px] ${tone === "rest" ? "text-mut" : "text-ink"}`}>{t.name}</span>
      <span className={`font-mono text-[11px] ${fg}`}>
        {t.done}/{t.total}
      </span>
    </Link>
  );
}

export function TrackAside({
  track,
  main,
  parallel,
  views,
  loading,
}: {
  track: RouteTrack;
  main: RouteTrack[];
  parallel: RouteTrack[];
  /** Документы открытых шагов трека — для «по треку». */
  views: StepView[];
  /** Хотя бы один открытый шаг ещё грузится — считать нечего. */
  loading: boolean;
}) {
  const opened = track.steps.filter((s) => s.state !== "locked").length;
  const partial = opened < track.total;
  const sum = (f: (v: StepView) => number) => (loading || opened === 0 ? "—" : String(views.reduce((a, v) => a + f(v), 0)));
  const stats = [
    { k: "шагов", v: String(track.total) },
    { k: partial ? "ресурсов в открытых шагах" : "ресурсов", v: sum((v) => v.content.resources.length) },
    { k: partial ? "задач практики в открытых шагах" : "задач практики", v: sum((v) => tasksTotal(v.content)) },
    { k: "средний темп когорты", v: "—" },
  ];
  const last = track.steps[track.steps.length - 1];
  const final = last && last.title.includes("Финальный проект") ? last : undefined;
  return (
    <aside data-testid="track-aside" className="flex flex-col gap-6">
      <section>
        <Eyebrow className="mb-2.5">место в маршруте</Eyebrow>
        <div className="flex flex-col gap-1">
          {main.map((t) => (
            <TrackRow key={t.id} t={t} current={t.id === track.id} />
          ))}
        </div>
        {parallel.length > 0 && (
          <div className="mt-3 pt-3" style={{ borderTop: "1px dashed var(--bd2)" }}>
            <Eyebrow className="mb-2">параллельно</Eyebrow>
            <div className="flex flex-col gap-1">
              {parallel.map((t) => (
                <TrackRow key={t.id} t={t} current={t.id === track.id} dashed />
              ))}
            </div>
          </div>
        )}
      </section>
      <section>
        <Eyebrow className="mb-2.5">по треку</Eyebrow>
        <dl className="m-0 grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-2 font-mono text-[11.5px]">
          {stats.map((s) => (
            <div key={s.k} className="contents">
              <dt className="text-mut">{s.k}</dt>
              <dd className="m-0 text-right text-ink" data-testid={`stat-${s.k}`}>
                {s.v}
              </dd>
            </div>
          ))}
        </dl>
      </section>
      {final && (
        <section className="rounded-md border border-bd bg-card p-4">
          <Eyebrow className="mb-2">финальный проект трека</Eyebrow>
          <div className="font-mono text-[13px] font-semibold text-ink">{final.title}</div>
          {track.slug === "ml" && (
            <>
              <p className="mb-2 mt-2 text-[12.5px] leading-[1.5] text-ink2">Один YAML-конфиг, одна команда на обучение, одна — на Docker. Конфиги: churn, house_prices, wine.</p>
              <p className="m-0 font-mono text-[11px] text-accdim">Оценивается корректность процедуры, не метрика.</p>
            </>
          )}
        </section>
      )}
    </aside>
  );
}
